import React, {type FC, Fragment, useRef, useState} from "react";
import {noop} from "lodash";

import {CompanyInfoTooltip, RoundedButton} from "components";
import IconUsers from "assets/icons/IconUsers";

import type {TIndustryCardProps} from "./types";
import styles from "./industryCard.module.scss";
import {STSingleCompany} from "api/companyApi/types";
import {setAPCompany} from "store/additional-profiles/additionalProfilesAsyncActions";
import {useAppDispatch} from "hooks";
import classNames from "classnames";
import {setProjectTabIndex} from "store/project-list/projectListActions";
import ProjectTabs from "mapx-pages/Project/ProjectTabs";
import {setActiveAccordionOnFilter} from "store/filter/filterActions";

const INITIAL_ITEMS_PER_PAGE = 15;

const IndustryCard: FC<TIndustryCardProps> = ({
	name,
	index,
	industry,
	setCompanyId,
	companyToWatch,
	currentCompanyId,
	setRemoveMouse = noop,
}) => {
	const dispatch = useAppDispatch();
	const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS_PER_PAGE);
	const hoverTimer = useRef<NodeJS.Timeout | null>(null);

	if (!name) return null;

	const mouseEnterHandler = (id: string) => {
		hoverTimer.current = setTimeout(() => {
			setRemoveMouse(true);
			setCompanyId(id);
		}, 800);
	};

	const handleMouseLeave = () => {
		if (hoverTimer.current) {
			clearTimeout(hoverTimer.current);
			hoverTimer.current = null;
		}
	};

	const applyCompanyOnFilter = (company: STSingleCompany) => {
		dispatch(setAPCompany({id: company}, true));

		dispatch(setProjectTabIndex(ProjectTabs.savedPeopleTabIndex));

		dispatch(setActiveAccordionOnFilter("Companies"));
	};

	const handleSeeMore = (count: number) => {
		setVisibleCount((prev) => prev + count);
	};

	const renderIndustry = industry?.companies
		?.slice(0, visibleCount)
		.map((company: STSingleCompany, index: number) => (
			<span key={`${company?.id}-${index}`} className={styles.companyContainer}>
				<div
					className={styles.companyContainer__skeleton}
					onClick={() => window.open(`/company/${company.id}`, "_blank")}
				>
					<img
						data-tooltip-delay-show={100}
						data-tooltip-id={String(currentCompanyId)}
						onMouseEnter={() => mouseEnterHandler(String(company?.id))}
						onMouseLeave={handleMouseLeave}
						onMouseOut={() => setRemoveMouse(false)}
						onBlur={() => setRemoveMouse(false)}
						className={styles.logo}
						src={
							company?.logo_url
								? company?.logo_url
								: "https://storage.googleapis.com/nebula-static/logos100x100/empty_logo.png"
						}
						alt={company?.name}
						style={{background: `url(${company.logo_url})`}}
					/>
					<span
						data-tooltip-delay-show={1000}
						data-tooltip-id={String(currentCompanyId)}
						onMouseOut={() => setRemoveMouse(false)}
						onBlur={() => setRemoveMouse(false)}
						onMouseEnter={() => mouseEnterHandler(String(company?.id))}
						onMouseLeave={handleMouseLeave}
						className={styles.companyName}
					>
						{company?.name}
					</span>
				</div>

				{company?.candidates_count > 0 && (
					<span
						className={styles.candidateCount}
						onClick={() => applyCompanyOnFilter(company)}
					>
						<IconUsers data-testid="candidate-count-icon" />
						<span className={styles.count}>{company?.candidates_count}</span>
					</span>
				)}
			</span>
		));

	return (
		<Fragment>
			<span className={styles.cardContainer}>
				<span className={styles.titleContainer}>
					{name}
					<span className={styles.industryCandidateCount}>
						{industry?.candidates_count !== 0 && <IconUsers />}
						{industry?.candidates_count || null}
					</span>
				</span>

				<span
					className={classNames(styles.contentContainer, {
						[styles.scrollableContentContainer]: industry?.companies?.length > 15,
					})}
				>
					{renderIndustry}
					{visibleCount < (industry?.companies?.length || 0) && (
						<div className={styles.showMore} data-testid="show-more">
							<RoundedButton
								className={styles.showMoreButton}
								onClick={() => handleSeeMore(50)}
							>
								Show More
							</RoundedButton>
						</div>
					)}
				</span>
			</span>

			{index === 0 && (
				<CompanyInfoTooltip
					id={String(currentCompanyId)}
					clearCompanyDetailsInfo
					company={companyToWatch}
				/>
			)}
		</Fragment>
	);
};

export default IndustryCard;
